// Per-user "last practice" memory.
//
// Logout clears the shared `practiceId` key (so the next account on the same
// browser never inherits it). Without a per-user memory, every sign-in then
// opened the FIRST practice alphabetically and multi-practice users had to
// switch every time — which also exposed the practice-switch permission race
// (RCA 2026-10-19). Keyed by user id so one person's practice never leaks into
// another account on a shared machine.

export { userKeyOf } from './authUser';

const keyFor = (userId) => `lastPracticeId:${userId}`;


export function rememberLastPractice(userId, practiceId) {
    if (userId == null || practiceId == null || practiceId === '' || practiceId === 'null') return;
    try {
        localStorage.setItem(keyFor(userId), String(practiceId));
    } catch {
        // storage unavailable (private mode / quota) — sign-in just falls back
    }
}

export function getLastPractice(userId) {
    if (userId == null) return null;
    try {
        const v = localStorage.getItem(keyFor(userId));
        return v && v !== 'null' && v !== 'undefined' ? v : null;
    } catch {
        return null;
    }
}

export function forgetLastPractice(userId) {
    if (userId == null) return;
    try {
        localStorage.removeItem(keyFor(userId));
    } catch {
        // ignore
    }
}

// The account-menu logout wipes ALL of localStorage (AccountPopover
// `localStorage.clear()`), which also erased every per-user last practice, so
// "reopen last practice" never worked after a normal sign-out (staging
// verification 2026-10-19). Snapshot these keys before a full clear and put
// them back afterwards; everything else is still cleared. Also kept: which desk
// this computer last claimed and the once-a-day sign-in desk prompt, or the
// prompt would forget its suggestion and re-ask after every sign-out (CU-869fmdxu8). The Team board's
// remembered practice choice is kept the same way (CU-869fnzh44).
const KEEP_PREFIXES = ['lastPracticeId:', 'outbound:lastDesk:', 'outbound:deskPrompt:', 'callLogs:teamBoard:practices:'];
const kept = (k) => KEEP_PREFIXES.some((p) => k.startsWith(p));

export function snapshotLastPractices() {
    const out = {};
    try {
        for (let i = 0; i < localStorage.length; i += 1) {
            const k = localStorage.key(i);
            if (k && kept(k)) out[k] = localStorage.getItem(k);
        }
    } catch {
        // storage unavailable
    }
    return out;
}

export function restoreLastPractices(snapshot) {
    try {
        Object.entries(snapshot || {}).forEach(([k, v]) => {
            if (kept(k) && v != null) localStorage.setItem(k, v);
        });
    } catch {
        // storage unavailable
    }
}
